import cn from 'classnames'
import Head from 'next/head'

import { Container } from '@components/Container'
import { AdvanceIcon } from '@components/icons/AdvanceIcon'
import { SelectButton } from '@components/SelectButton'
import SelectPerPage from '@components/Pagination/components/SelectPerPage/SelectPerPage'
import StoryCardSkeleton from '@components/StoryCardSkeleton'
import NoData from '@components/NoData'
import { StoryCard } from '@components/StoryCard'
import { Pagination } from '@components/Pagination'
import { CategoryDetail } from '@components/CategoryDetail'
import { PrimaryTypeEnum } from '@interfaces/PrimaryTypeEnum'
import { AdvancedSearchForm } from '@features/allCategory/components/AdvancedSearchForm'
import { SearchRecommendType } from '@models/recommend/SearchRecommendType'
import { SelectLine } from '@components/SelectLine'
import { AllCategoryPageProps } from './interface'

const perPageOptions = [
  {
    label: '10',
    value: 10,
  },
  {
    label: '20',
    value: 20,
  },
  {
    label: '30',
    value: 30,
  },
]

export function AllCategoryPage({
  data,
  page,
  perpage,
  total,
  title,
  categories,
  allCategorySearchParam,
  categoryDetail,
  showAdvancedSearch,
  isLoading,
  isLoadingToggleBookLike,
  handleCategoryChange,
  handlePrimaryTypeChange,
  handleClickAdvancedSearchButton,
  getOrderBy,
  perpageChange,
  handleLikeBook,
  handlePageChange,
  openStoryDrawer,
  handleSubmit,
}: AllCategoryPageProps) {
  return (
    <Container className='mobile:px-[20px] !pb-[80px]'>
      <Head>
        <title>{`อ่าน${title}ออนไลน์ อ่านจุใจ ฟินกว่าใครที่ MaReads`}</title>
      </Head>
      <h1 className='text-[24px] text-primary font-mitr font-medium mb-[20px]'>
        {title}
      </h1>
      <SelectButton
        selectClassName='mb-[30px]'
        options={categories}
        value={allCategorySearchParam.categoryId}
        onChange={handleCategoryChange}
      />
      <div id='all-category-list' className='flex mb-[20px] mobile:flex-col'>
        <SelectLine
          value={allCategorySearchParam.primaryType}
          options={[
            {
              value: PrimaryTypeEnum.TOP_CHART,
              text: 'ท็อปชาร์ต',
            },
            { value: PrimaryTypeEnum.NEW, text: 'มาใหม่' },
            { value: PrimaryTypeEnum.HOT_NEW, text: 'ฮิตติดเทรนด์' },
            { value: PrimaryTypeEnum.ENDED, text: 'จบแล้ว' },
            { value: PrimaryTypeEnum.ALL, text: 'ทั้งหมด' },
          ]}
          onChange={handlePrimaryTypeChange}
        />
        <button
          className={cn(
            'flex items-center justify-center text-[14px] desktop-tablet:ml-[10px] mobile:mt-[10px] font-mitr font-medium whitespace-nowrap py-[8px] px-[20px] border border-secondary rounded-[20px]',
            {
              'bg-white dark:bg-dark-7 text-sencondary dark:text-dark-10 dark:border-dark-10':
                showAdvancedSearch,
              'bg-secondary dark:bg-purple text-white dark:text-white-2':
                !showAdvancedSearch,
            }
          )}
          type='button'
          onClick={handleClickAdvancedSearchButton}
        >
          <AdvanceIcon className='mr-[5px]' />
          การค้นหาขั้นสูง
        </button>
      </div>
      <AdvancedSearchForm
        className={cn({ hidden: !showAdvancedSearch })}
        orderBy={allCategorySearchParam.orderBy}
        primaryType={allCategorySearchParam.primaryType}
        secondaryType={allCategorySearchParam.secondaryType}
        showAdvancedSearch={showAdvancedSearch}
        getOrderBy={getOrderBy}
        handleSubmit={handleSubmit}
      />
      <div className='flex justify-between items-center tablet-mobile:px-[20px] mb-5'>
        <div className='text-secondary-1 text-sm font-light'>
          <span className='mobile:hidden'>จำนวนงานเขียน:</span>
          <span className='text-secondary dark:text-dark-10 font-bold'>{` ${total.toLocaleString()} `}</span>
          รายการ
        </div>
        <div className='flex justify-end'>
          <SelectPerPage
            value={perpage}
            options={perPageOptions}
            handleSelect={perpageChange}
          />
        </div>
      </div>

      {isLoading ? (
        <section className='grid grid-cols-2 tablet:grid-cols-4 gap-5 mb-[30px]'>
          {[...Array(2)].map((l, i) => (
            <StoryCardSkeleton key={i} direction='horizontal' />
          ))}
        </section>
      ) : data.length ? (
        <>
          <section className='grid grid-cols-2 mobile:gap-[10px] tablet:grid-cols-4 gap-[20px] mb-[30px]'>
            {data.map((story: SearchRecommendType) => (
              <StoryCard
                key={story.id}
                id={story.id}
                isShowBookPromotion={story.hasSalePromotions}
                buyAllChaptersPromotion={story.buyAllChaptersPromotion}
                bookChapterCampaignHeader={story.bookChapterCampaignHeader}
                readFreePromotion={story.readFreePromotion}
                bookBadges={story.bookBadges}
                title={story.title}
                imageUrl={story.coverImgPath}
                penName={story.penName?.firstPenName}
                slug={story.penName?.user?.profilePageSlug}
                content={story.cover}
                category={story.category?.name}
                tags={story.tags}
                chapter={story.chapterCount}
                view={story.viewCount}
                chat={story.commentCount}
                like={story.likeCount}
                isLiked={story.isLiked}
                isEnded={story.isEnded}
                bookType={story.bookType}
                isToggleBookLike={isLoadingToggleBookLike}
                handleLikeBook={handleLikeBook}
                categoryId={story.category?.id}
                handleClickStoryCard={() => {
                  openStoryDrawer(story)
                }}
              />
            ))}
          </section>
          <Pagination
            page={page}
            perpage={perpage}
            total={total}
            pageChange={handlePageChange}
          />
        </>
      ) : (
        <NoData />
      )}
      <hr className='text-gray-3 dark:text-purple my-[50px]' />
      <CategoryDetail title={title} description={categoryDetail} />
    </Container>
  )
}
